/* One initiative: the outcome, the bets it serves, and all of the work behind
   it in one place.

   The three lists are the existing list formats rather than new ones, because
   somebody arriving here from the portfolio should recognise what they are
   looking at. Tasks show whether their initiative is inherited from their
   object or set on the task, which is the difference between work that moves
   when a service is reassigned and work that does not. */
import { useCallback, useEffect, useState } from "react";

import { ApiError, api, type Initiative } from "../api";
import { usePermissions } from "../auth";
import { Chip, DueDate, Field, Panel, Score, priorityTone } from "../components/ui";
import { BetChips, GapBadges, statusTone, QuarterSelect } from "../components/strategy";
import { go, href } from "../route";
import { useStore } from "../store";

export default function InitiativeDetail({ id }: { id: number }) {
  const { meta, bets, people, run, refresh } = useStore();
  const { isAdmin } = usePermissions();
  const [initiative, setInitiative] = useState<Initiative | null>(null);
  const [missing, setMissing] = useState(false);
  const [blocked, setBlocked] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setInitiative(await api.initiative(id));
      setMissing(false);
    } catch {
      setMissing(true);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (missing) return <p className="note">No such initiative. It may have been deleted.</p>;
  if (!initiative || !meta) return <p className="note">Loading…</p>;

  const patch = async (body: Record<string, unknown>) => {
    const done = await run(() => api.patchInitiative(initiative.id, body));
    if (done) {
      await refresh();
      await load();
    }
  };

  const r = initiative.rollup;

  return (
    <>
      <p className="crumbs">
        <a className="crumb" href={href.strategy}>
          Strategy
        </a>{" "}
        / {initiative.name}
      </p>
      <div className="panel-hd" style={{ marginBottom: 14 }}>
        <h1 style={{ fontFamily: "var(--display)", fontSize: 22, margin: 0 }}>
          <span className="icode">{initiative.code}</span> {initiative.name}
        </h1>
        <span className="row tight">
          <BetChips betIds={initiative.bet_ids} />
          <Chip text={initiative.status} tone={statusTone(initiative.status)} />
          {r && r.maturity !== null && (
            <span className="num" style={{ fontSize: 20, fontWeight: 700 }}>
              <Score value={r.maturity} />
            </span>
          )}
          <GapBadges gaps={initiative.gaps} />
        </span>
      </div>

      {blocked && <div className="warnbox">{blocked}</div>}

      <div className="detail">
        <div>
          <Panel title="Details">
            {isAdmin ? (
              <>
                <div className="row">
                  <Field label="Code">
                    <input
                      type="text"
                      defaultValue={initiative.code}
                      key={`code-${initiative.updated_at}`}
                      style={{ minWidth: 90 }}
                      onBlur={(e) =>
                        e.target.value !== initiative.code && patch({ code: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Name">
                    <input
                      type="text"
                      defaultValue={initiative.name}
                      key={`name-${initiative.updated_at}`}
                      onBlur={(e) =>
                        e.target.value !== initiative.name && patch({ name: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Status">
                    <select
                      value={initiative.status}
                      onChange={(e) => patch({ status: e.target.value })}
                    >
                      {meta.initiative_statuses.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Owner">
                    <select
                      value={initiative.owner_id ?? ""}
                      onChange={(e) =>
                        patch({ owner_id: e.target.value === "" ? null : Number(e.target.value) })
                      }
                    >
                      <option value="">Unassigned</option>
                      {people.map((p) => (
                        <option key={p.person_id} value={p.person_id}>
                          {p.display_name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Start">
                    <QuarterSelect
                      value={initiative.start_quarter}
                      quarters={meta.quarters}
                      onChange={(v) => patch({ start_quarter: v })}
                    />
                  </Field>
                  <Field label="Target">
                    <QuarterSelect
                      value={initiative.target_quarter}
                      quarters={meta.quarters}
                      onChange={(v) => patch({ target_quarter: v })}
                    />
                  </Field>
                </div>
                <Field label="Description">
                  <textarea
                    rows={6}
                    defaultValue={initiative.description}
                    key={`desc-${initiative.id}`}
                    onBlur={(e) =>
                      e.target.value !== initiative.description &&
                      patch({ description: e.target.value })
                    }
                  />
                </Field>
                <Field label="Bets it serves">
                  <div className="row tight">
                    {bets.length === 0 && <span className="muted">No bets exist yet.</span>}
                    {bets.map((b) => (
                      <label key={b.id} className="checkline">
                        <input
                          type="checkbox"
                          checked={initiative.bet_ids.includes(b.id)}
                          onChange={(e) =>
                            patch({
                              bet_ids: e.target.checked
                                ? [...initiative.bet_ids, b.id]
                                : initiative.bet_ids.filter((x) => x !== b.id),
                            })
                          }
                        />
                        <span className="bcode">{b.code}</span> {b.name}
                      </label>
                    ))}
                  </div>
                </Field>
              </>
            ) : (
              <>
                <p className="meta-line">
                  Owner {initiative.owner_name ?? "—"}
                  {initiative.start_quarter && ` · ${initiative.start_quarter}`}
                  {initiative.target_quarter && ` → ${initiative.target_quarter}`}
                </p>
                <p className="prose">{initiative.description || "No description."}</p>
              </>
            )}
          </Panel>

          <Panel
            title="Services"
            aside={
              <span className="count">
                {r?.services_active ?? 0} under way
                {r && r.services_idea > 0 ? ` · ${r.services_idea} at Idea` : ""}
                {r && r.services_retired > 0 ? ` · ${r.services_retired} end of life` : ""}
              </span>
            }
          >
            <ObjectList rows={initiative.services ?? []} />
          </Panel>

          <Panel title="Assets" aside={<span className="count">{r?.assets ?? 0}</span>}>
            <ObjectList rows={initiative.assets ?? []} />
          </Panel>

          <Panel
            title="Tasks"
            aside={
              <span className="count">
                {r ? `${r.tasks_open + r.tasks_in_progress} open of ${r.tasks}` : ""}
                {r && r.tasks_overdue > 0 ? ` · ${r.tasks_overdue} overdue` : ""}
              </span>
            }
          >
            {(initiative.tasks ?? []).length === 0 ? (
              <p className="note">No tasks resolve to this initiative.</p>
            ) : (
              <div className="tablewrap">
                <table>
                  <thead>
                    <tr>
                      <th className="left">Task</th>
                      <th className="left">Status</th>
                      <th className="left">Owner</th>
                      <th className="left">Due</th>
                      <th className="left">Via</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(initiative.tasks ?? []).map((t) => (
                      <tr key={t.id}>
                        <td className="left">
                          <a href={href.task(t.id)}>{t.title}</a>
                        </td>
                        <td className="left muted">{t.status}</td>
                        <td className="left muted">{t.owner_name ?? "—"}</td>
                        <td className="left">
                          <DueDate date={t.next_milestone_date} status={t.status} />
                        </td>
                        <td className="left muted">
                          {t.initiative?.source === "inherited" && t.attached_to ? (
                            <a href={href.object(t.attached_to.kind, t.attached_to.id)}>
                              {t.attached_to.name}
                            </a>
                          ) : (
                            <span className="tagline">set on the task</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {isAdmin && (
            <Panel title="Danger">
              <button
                className="btn"
                onClick={async () => {
                  if (!confirm(`Delete the initiative "${initiative.name}"?`)) return;
                  setBlocked(null);
                  try {
                    await api.removeInitiative(initiative.id);
                    await refresh();
                    go(href.strategy);
                  } catch (e) {
                    /* 409 carries {message, blocked_by} rather than a plain
                       sentence, so the status line is not what to show. Shown
                       here rather than in the global banner because the next
                       action -- reassigning those items -- is on this page. */
                    const detail =
                      e instanceof ApiError && e.detail && typeof e.detail === "object"
                        ? (e.detail as { message?: string }).message
                        : null;
                    setBlocked(detail ?? (e instanceof Error ? e.message : String(e)));
                  }
                }}
              >
                Delete this initiative
              </button>
            </Panel>
          )}
        </div>
      </div>
    </>
  );
}

function ObjectList({ rows }: { rows: Initiative["services"] }) {
  if (!rows || rows.length === 0) return <p className="note">Nothing assigned yet.</p>;
  return (
    <div className="tablewrap">
      <table>
        <thead>
          <tr>
            <th className="left">Name</th>
            <th className="left">Class</th>
            <th className="left">Stage</th>
            <th className="left">Owner</th>
            <th className="left">Pri</th>
            <th className="num">Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={`${o.kind}-${o.id}`}>
              <td className="left">
                <a href={href.object(o.kind, o.id)}>{o.name}</a>
              </td>
              <td className="left muted">{o.class}</td>
              <td className="left muted">{o.lifecycle_stage}</td>
              <td className="left muted">{o.owner_name ?? "—"}</td>
              <td className="left">
                <Chip text={o.priority} tone={priorityTone(o.priority)} />
              </td>
              <td className="num">
                <Score value={o.maturity.score} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
